import { useEffect, useState } from 'react';

import { Burger, Drawer, Group, Stack, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import {
  IconClock,
  IconGift,
  IconHome,
  IconInfoCircle,
  IconMusic,
  IconPhoto,
  IconShirt,
} from '@tabler/icons-react';

import { PRIMARY_FONT } from '../../constants/fonts';
import { useScrollSpy } from '../../hooks/useScrollSpy';

const NavigationMenu = () => {
  const theme = useMantineTheme();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const activeSection = useScrollSpy([
    'home',
    'info',
    'timeline',
    'details',
    'gifts',
    'music',
    'gallery',
  ]);

  const navigationItems = [
    { id: 'home', label: 'Start', icon: IconHome },
    { id: 'info', label: 'Informacje', icon: IconInfoCircle },
    { id: 'timeline', label: 'Harmonogram', icon: IconClock },
    // { id: 'details', label: 'Szczegóły', icon: IconShirt },
    { id: 'music', label: 'Muzyka', icon: IconMusic },
    { id: 'gifts', label: 'Prezenty', icon: IconGift },
    { id: 'gallery', label: 'Galeria', icon: IconPhoto },
  ];

  const scrollToSection = (sectionId: string, updateHash: boolean = true) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      
      // Update URL hash without triggering page reload
      if (updateHash) {
        window.history.pushState(null, '', `#${sectionId}`);
      }
    }
    closeDrawer();
  };

  // Handle initial page load with hash
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.slice(1); // Remove the '#' from hash
      if (hash) {
        // Small delay to ensure the page is fully loaded
        setTimeout(() => {
          scrollToSection(hash, false); // Don't update hash when navigating from hash
        }, 100);
      }
    };

    // Handle initial load
    handleHashNavigation();

    // Handle browser back/forward navigation
    window.addEventListener('popstate', handleHashNavigation);

    return () => {
      window.removeEventListener('popstate', handleHashNavigation);
    };
  }, []);

  return (
    <>
      {/* Desktop Navigation */}
      <Group gap="md" visibleFrom="md">
        {navigationItems.map(item => (
          <UnstyledButton
            key={item.id}
            onClick={() => scrollToSection(item.id)}
            style={{
              padding: '10px 16px',
              borderRadius: '12px',
              background: activeSection === item.id ? `rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.2)` : 'transparent',
              backdropFilter: activeSection === item.id ? 'blur(10px)' : 'none',
              border:
                activeSection === item.id
                  ? `1px solid rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.3)`
                  : '1px solid transparent',
              color: activeSection === item.id ? theme.other.titleColor : '#6b7280',
              fontWeight: activeSection === item.id ? 600 : 500,
              fontFamily: PRIMARY_FONT,
              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              boxShadow: activeSection === item.id ? '0 4px 16px rgba(0, 0, 0, 0.1)' : 'none',
            }}
            styles={{
              root: {
                '&:hover': {
                  background: `rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.15)`,
                  backdropFilter: 'blur(8px)',
                  transform: 'translateY(-1px)',
                },
              },
            }}
          >
            <Group gap="xs">
              <item.icon size={16} />
              <Text
                size="sm"
                style={{
                  fontFamily: PRIMARY_FONT,
                  fontWeight: 'inherit',
                }}
              >
                {item.label}
              </Text>
            </Group>
          </UnstyledButton>
        ))}
      </Group>

      {/* Mobile Navigation */}
      <Group hiddenFrom="md">
        <Burger opened={drawerOpened} onClick={toggleDrawer} size="sm" color={theme.other.titleColor} />
      </Group>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Menu"
        padding="md"
        size="xs"
        position="right"
        styles={{
          header: {
            fontFamily: PRIMARY_FONT,
            fontWeight: 600,
          },
          body: {
            background: 'rgba(248, 250, 252, 0.95)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Stack gap="xs">
          {navigationItems.map(item => (
            <UnstyledButton
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                background: activeSection === item.id ? `rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.2)` : 'transparent',
                backdropFilter: activeSection === item.id ? 'blur(10px)' : 'none',
                border:
                  activeSection === item.id
                    ? `1px solid rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.3)`
                    : '1px solid transparent',
                color: activeSection === item.id ? theme.other.titleColor : '#6b7280',
                fontWeight: activeSection === item.id ? 600 : 500,
                fontFamily: PRIMARY_FONT,
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                boxShadow: activeSection === item.id ? '0 4px 16px rgba(0, 0, 0, 0.1)' : 'none',
              }}
              styles={{
                root: {
                  '&:hover': {
                    background: `rgba(${parseInt(theme.other.titleColor.slice(1, 3), 16)}, ${parseInt(theme.other.titleColor.slice(3, 5), 16)}, ${parseInt(theme.other.titleColor.slice(5, 7), 16)}, 0.15)`,
                    backdropFilter: 'blur(8px)',
                    transform: 'translateX(4px)',
                  },
                },
              }}
            >
              <Group gap="sm">
                <item.icon size={18} />
                <Text
                  size="md"
                  style={{
                    fontFamily: PRIMARY_FONT,
                    fontWeight: 'inherit',
                  }}
                >
                  {item.label}
                </Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </Drawer>
    </>
  );
};

export { NavigationMenu };
export default NavigationMenu;
